import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Robot, PaperPlaneTilt, ChatCircleDots } from "@phosphor-icons/react";

export default function WidgetDemo() {
  const [params] = useSearchParams();
  const [open, setOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(`pub-${Math.random().toString(36).slice(2, 10)}`);
  const [config, setConfig] = useState({ business_name: "Acme Co", welcome_message: "Hi! How can I help today?" });
  const userId = params.get("u") || "demo";
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const u = { role: "user", content: input };
    setMessages((m) => [...m, u]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/public/bot/chat", {
        user_id: userId,
        message: u.content,
        session_id: sessionId,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Bot not configured for this workspace." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-soft)] grid-bg">
      <div className="max-w-4xl mx-auto px-8 py-16">
        <div className="cell-strong bg-white p-12">
          <div className="overline text-[var(--ink-soft)]">/ Demo page</div>
          <h1 className="font-display text-4xl mt-3 mb-4">This is your customer's website.</h1>
          <p className="text-[var(--ink-soft)] mb-6">Imagine the marketing copy, the product photos, the "About Us"… and over there, in the corner, your AI employee waiting to help.</p>
          <div className="cell p-6 bg-[var(--bg-soft)] font-mono-rc text-xs">
            workspace: {userId}<br />
            session: {sessionId}
          </div>
        </div>
      </div>

      {/* Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        {open ? (
          <div className="cell-strong bg-white w-[360px] h-[480px] flex flex-col" data-testid="widget-panel">
            <div className="px-4 py-3 bg-[var(--ink)] text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[var(--accent)] flex items-center justify-center"><Robot size={16} weight="duotone" color="#0A0A0A" /></div>
                <div>
                  <div className="font-medium text-sm">{config.business_name}</div>
                  <div className="text-xs text-white/60 font-mono-rc">● ONLINE</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white text-xl">×</button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-auto p-3 space-y-2 bg-[var(--bg-soft)]">
              <Bubble role="assistant">{config.welcome_message}</Bubble>
              {messages.map((m, i) => <Bubble key={i} role={m.role}>{m.content}</Bubble>)}
              {busy && <Bubble role="assistant">…</Bubble>}
            </div>
            <form onSubmit={send} className="p-2 border-t border-[var(--line)] flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a question…" className="input-rc text-sm" data-testid="widget-input" />
              <button type="submit" disabled={busy} className="btn-primary px-3" data-testid="widget-send"><PaperPlaneTilt size={16} weight="fill" /></button>
            </form>
          </div>
        ) : (
          <button onClick={() => setOpen(true)} className="w-14 h-14 bg-[var(--brand)] text-white shadow-lg flex items-center justify-center hover:scale-105 transition" data-testid="widget-open-btn">
            <ChatCircleDots size={24} weight="fill" />
          </button>
        )}
      </div>
    </div>
  );
}

function Bubble({ role, children }) {
  if (role === "user") {
    return <div className="flex justify-end"><div className="bg-[var(--brand)] text-white px-3 py-2 max-w-[85%] text-sm">{children}</div></div>;
  }
  return <div className="flex"><div className="bg-white border border-[var(--ink)] px-3 py-2 max-w-[85%] text-sm">{children}</div></div>;
}
